import type { Dispatch, SetStateAction } from 'react';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, Badge, Button, Callout, ConfidenceDot, ProgressBar } from '@/shared/ui';
import { DAYS, TIME_OPTIONS } from '../utils/officeHoursHelpers';
import { FIELDS } from '../constants/profileFields';
import { SectionHeader } from '../components/SectionHeader';
import { StatusPills } from '../components/StatusPills';
import { MembershipPlansEditor } from './MembershipPlansEditor';
import type { OfficeHours, MembershipPlan } from '../api/extractionApi';
import styles from './PracticeProfileStep.module.css';

export type FieldState = Record<
  string,
  { value: string; confirmed: boolean; editing: boolean; prevConfirmed?: boolean }
>;

interface Props {
  fields: FieldState;
  setField: (key: string, patch: Partial<FieldState[string]>) => void;
  confirmSingle: (key: string) => void;
  confirmAll: () => void;
  confirmMutationPending: boolean;
  officeHoursLocal: OfficeHours;
  setOfficeHoursLocal: Dispatch<SetStateAction<OfficeHours>>;
  membershipPlansLocal: MembershipPlan[];
  setMembershipPlansLocal: Dispatch<SetStateAction<MembershipPlan[]>>;
  confirmedCount: number;
  totalFields: number;
  section1Done: boolean;
  section1FillStatus: 'filled' | 'partially-filled' | null;
  section1Expanded: boolean;
  setSection1Expanded: Dispatch<SetStateAction<boolean>>;
}

export function PracticeDataSection({
  fields,
  setField,
  confirmSingle,
  confirmAll,
  confirmMutationPending,
  officeHoursLocal,
  setOfficeHoursLocal,
  membershipPlansLocal,
  setMembershipPlansLocal,
  confirmedCount,
  totalFields,
  section1Done,
  section1FillStatus,
  section1Expanded,
  setSection1Expanded,
}: Props) {
  return (
    <>
      <SectionHeader n={1} done={section1Done} title="Your practice data" estimate="~2 min" />
      <p className={styles.sectionSubtitle}>
        AI Pre-Filled from your website &amp; public data. Review each field. Confirm if correct, or
        Edit to update.
      </p>

      <Card padding="none" className={styles.accordionCard}>
        <div onClick={() => setSection1Expanded((p) => !p)} className={styles.accordionHeader}>
          {section1Done && (
            <span className={styles.accentIcon}>
              <Check size={14} strokeWidth={2.5} />
            </span>
          )}
          <span className={styles.accordionHeaderTitle}>Practice details</span>
          <StatusPills fill={section1FillStatus} source="AI" confirmed={section1Done} />
          <span className={styles.accordionChevron}>
            {section1Expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </span>
        </div>

        {section1Expanded && (
          <div className={styles.accordionBodySm}>
            {FIELDS.map((f) => {
              const state = fields[f.key];
              if (!state) return null;

              const isOfficeHours = f.key === 'officeHours';
              const isMembershipPlans = f.key === 'membershipPlans';

              return (
                <Card key={f.key} padding="sm">
                  <div className={styles.fieldHeader}>
                    <div className={styles.fieldLabelRow}>
                      <span className={styles.fieldLabelText}>{f.label}</span>
                      {!state.confirmed && <ConfidenceDot level={f.confidence} />}
                    </div>
                    <div className={styles.headerActions}>
                      {state.confirmed ? (
                        <>
                          <Badge variant="primary">
                            <span className={styles.badgeContent}>
                              <Check size={11} aria-hidden /> Confirmed
                            </span>
                          </Badge>
                          {!f.readonly && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                setField(f.key, {
                                  confirmed: false,
                                  editing: true,
                                  prevConfirmed: true,
                                })
                              }
                            >
                              Edit
                            </Button>
                          )}
                        </>
                      ) : (
                        <div className={styles.headerButtons}>
                          {!f.readonly && (
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => confirmSingle(f.key)}
                            >
                              Confirm
                            </Button>
                          )}
                          {!f.readonly && (
                            <Button
                              size="sm"
                              variant={state.editing ? 'ghost' : 'secondary'}
                              onClick={() =>
                                setField(f.key, {
                                  editing: !state.editing,
                                  ...(state.editing && state.prevConfirmed
                                    ? { confirmed: true, prevConfirmed: false }
                                    : {}),
                                })
                              }
                            >
                              {state.editing ? 'Cancel' : 'Edit'}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {state.editing ? (
                    isOfficeHours ? (
                      <div className={styles.daysEditor}>
                        {DAYS.map(({ key, short }) => {
                          const dh = officeHoursLocal[key] ?? null;
                          const isOpen = dh !== null;
                          return (
                            <div key={key} className={styles.dayRow}>
                              <span className={styles.dayLabel}>{short}</span>
                              <div
                                className={`${styles.toggle} ${isOpen ? styles.toggleOn : styles.toggleOff}`}
                                onClick={() =>
                                  setOfficeHoursLocal((prev) => ({
                                    ...prev,
                                    [key]: isOpen ? null : { open: '08:00', close: '17:00' },
                                  }))
                                }
                              >
                                <div
                                  className={`${styles.toggleKnob} ${isOpen ? styles.toggleKnobOn : ''}`}
                                />
                              </div>
                              {isOpen ? (
                                <div className={styles.timeRow}>
                                  <select
                                    value={dh!.open}
                                    onChange={(e) =>
                                      setOfficeHoursLocal((prev) => ({
                                        ...prev,
                                        [key]: { ...prev[key]!, open: e.target.value },
                                      }))
                                    }
                                    className={styles.timeSelect}
                                  >
                                    {TIME_OPTIONS.map((t) => (
                                      <option key={t.value} value={t.value}>
                                        {t.label}
                                      </option>
                                    ))}
                                  </select>
                                  <span className={styles.timeDash}>–</span>
                                  <select
                                    value={dh!.close}
                                    onChange={(e) =>
                                      setOfficeHoursLocal((prev) => ({
                                        ...prev,
                                        [key]: { ...prev[key]!, close: e.target.value },
                                      }))
                                    }
                                    className={styles.timeSelect}
                                  >
                                    {TIME_OPTIONS.map((t) => (
                                      <option key={t.value} value={t.value}>
                                        {t.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              ) : (
                                <span className={styles.closedLabel}>Closed</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : isMembershipPlans ? (
                      <MembershipPlansEditor
                        plans={membershipPlansLocal}
                        onChange={setMembershipPlansLocal}
                        onConfirm={() => {
                          setField(f.key, {
                            value:
                              membershipPlansLocal
                                .map((p) => `${p.name}${p.cost ? ` (${p.cost})` : ''}`)
                                .join(', ') || 'No membership plans',
                          });
                          confirmSingle(f.key);
                        }}
                      />
                    ) : (
                      <div className={styles.editorCol}>
                        {/* Grows with its content via CSS (a hidden copy of the
                            text in the same grid cell sets the height). */}
                        <div className={styles.autoGrow} data-value={state.value}>
                          <textarea
                            value={state.value}
                            placeholder={f.emptyLabel}
                            onChange={(e) => setField(f.key, { value: e.target.value })}
                            rows={1}
                            className={styles.editorTextarea}
                          />
                        </div>
                      </div>
                    )
                  ) : (
                    <div className={styles.fieldValue}>{state.value || f.emptyLabel}</div>
                  )}
                </Card>
              );
            })}

            <div className={styles.progressFooter}>
              <span className={styles.progressNote}>
                {confirmedCount}/{totalFields} confirmed
              </span>
              <ProgressBar value={confirmedCount} max={totalFields} height={4} />
            </div>

            {!section1Done ? (
              <div className={styles.unconfirmedActions}>
                <Callout variant="warning">
                  {totalFields - confirmedCount} unconfirmed fields — AI will use these as-is if you
                  proceed
                </Callout>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={confirmAll}
                  disabled={confirmMutationPending}
                >
                  {confirmMutationPending ? 'Saving...' : 'Confirm All & Continue'}
                </Button>
              </div>
            ) : (
              <div className={styles.allConfirmedRow}>
                <Badge variant="primary" size="sm">
                  <span className={styles.badgeContent}>
                    <Check size={12} aria-hidden /> All fields confirmed
                  </span>
                </Badge>
              </div>
            )}
          </div>
        )}
      </Card>
    </>
  );
}
